/** Merchant naming and selection shared by product rendering and freshness. */
import { merchantKey } from '@/lib/offer-comparison-state'

export interface DisplayOfferSource {
  retailers?: { name?: string | null; network?: string | null } | null
  advertisers?: { advertiser_name?: string | null } | null
  _siblingAdvertiserName?: string | null
}

export function displayOfferRetailer(offer: DisplayOfferSource, productAdvertiser?: string | null): string {
  const retailer = offer.retailers?.name || 'Unknown'
  const generic = ['CJ Affiliate', 'FlexOffers', 'ShareASale', 'Awin', 'Impact'].includes(retailer)
  const display = generic
    ? offer.advertisers?.advertiser_name || offer._siblingAdvertiserName || productAdvertiser || retailer
    : retailer
  const lower = display.toLowerCase()
  return lower.includes('amazon marketplace') || lower === 'amazon' ? 'Amazon' : display
}

export interface DisplayOfferCandidate {
  id: string
  retailer: string
  price: number
  lastChecked?: string | null
  priceStale?: boolean
  _network: string
}

/** Shared visible-price clock, also used by the sitemap selection replay. */
export function isDisplayPriceStale(lastChecked: string | null | undefined, now = Date.now()): boolean {
  const checked = lastChecked ? Date.parse(lastChecked) : NaN
  return !Number.isFinite(checked) || now - checked > 30 * 24 * 60 * 60 * 1000
}

/**
 * FRIDGE-1238: the clock a visible price is judged by. A FlexOffers offer
 * carries the feed's own build date in `price_verified_at` (the price is
 * exactly that old, whatever day the file was downloaded), so the 30-day
 * visible window and the 7-day schema window read that date first. Every
 * other network keeps the download clock (`last_checked_at`): the owner's
 * 07/20/2026 windows were set against it, and Amazon's `price_verified_at`
 * is a feed reading with its own contract (lib/price-authority.ts). This is
 * decision (b) on FRIDGE-1238, taken 10/09/2026: frozen FlexOffers feeds
 * become honest without moving Amazon.
 */
export function visiblePriceClock(offer: {
  last_checked_at?: string | null
  price_verified_at?: string | null
  retailers?: { network?: string | null } | null
}): string | null {
  const network = (offer.retailers?.network ?? '').toLowerCase()
  if (network === 'flexoffers' && offer.price_verified_at) return offer.price_verified_at
  return offer.last_checked_at ?? null
}

/**
 * FRIDGE-1643: the ONE rule for whether the product page shows an offer's
 * price or "Price Unavailable": no finite positive price, or a price the
 * server marked stale (`priceStale`, set from isDisplayPriceStale on the
 * offer's visiblePriceClock). The offers card, the buy-box candidates and the
 * Product JSON-LD's offer filter all call this, so structured data lists
 * exactly the offers the page shows with a price. The units of `price` do not
 * matter (cents on the page, dollars in the schema builder).
 */
export function isOfferPriceUnavailable(offer: { price?: number | null; priceStale?: boolean | null }): boolean {
  return typeof offer.price !== 'number' || !Number.isFinite(offer.price) || offer.price <= 0 || offer.priceStale === true
}

function hasFreshPrice(offer: DisplayOfferCandidate): boolean {
  return !isOfferPriceUnavailable(offer) && !isDisplayPriceStale(offer.lastChecked)
}

/** Fresh usable price first, then lowest price, network and stable identity. */
export function selectDisplayedOffers<T extends DisplayOfferCandidate>(offers: readonly T[]): T[] {
  const priority: Record<string, number> = { cj: 1, flexoffers: 2, amazon: 0 }
  const selected = new Map<string, T>()
  for (const offer of offers) {
    // FRIDGE-117: two network labels for one merchant are one choice.
    // Keep the winning row unchanged; normalization is for identity only.
    const key = merchantKey(offer.retailer)
    const existing = selected.get(key)
    const rank = priority[offer._network] ?? 99
    const existingRank = existing ? priority[existing._network] ?? 99 : 99
    const fresh = hasFreshPrice(offer)
    const existingFresh = existing ? hasFreshPrice(existing) : false
    if (!existing || (fresh && !existingFresh) || (fresh === existingFresh && (
      offer.price < existing.price || (offer.price === existing.price && (
        rank < existingRank || (rank === existingRank && offer.id < existing.id)
      ))
    ))) selected.set(key, offer)
  }
  return [...selected.values()].sort((a, b) => a.price - b.price || a.id.localeCompare(b.id))
}
